/** Colours a priority can be shown in. Null = the colour of its level. */
export const SUPPORT_PRIORITY_COLORS = ['red', 'amber', 'blue', 'green', 'gray'];
/**
 * Policy §12 — the working week due times are counted in (Priority policy →
 * Working hours). Off: calendar time.
 */
export const SUPPORT_WORKING_DAY_MODES = ['off', 'all', 'urgent'];
export const SUPPORT_WORKING_DAY_MODE_LABELS = {
    off: 'Day off',
    all: 'Working day',
    urgent: 'Urgent only',
};
/** Sunday first, as the days are stored. */
export const SUPPORT_WEEKDAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
export const DEFAULT_SUPPORT_INTAKE_SETTINGS = {
    requireName: false,
    requireEmail: false,
    requirePhone: false,
    confirmContractId: false,
    maxContractIdTries: 2,
    replyQuietSeconds: 5,
    maxReplyWaitSeconds: 20,
    typingIndicator: true,
};
/** Where the server says the conversation is. The agent only phrases it. */
export const SUPPORT_INTAKE_STEPS = [
    'listen',
    'name',
    'email',
    'phone',
    'confirm_contract',
    'contract_id_unauthorized',
    'contract_id_unknown',
    'no_contract',
    'issue',
];
/** The one thing still needed before a ticket may open. */
export const SUPPORT_INTAKE_GAPS = ['name', 'email', 'phone', 'contract'];
/** Sun–Thu 09:00–17:00, Friday off, Saturday for urgent tickets (policy §12). */
export const DEFAULT_SUPPORT_WORKING_DAYS = [
    { mode: 'all', from: '09:00', to: '17:00' },
    { mode: 'all', from: '09:00', to: '17:00' },
    { mode: 'all', from: '09:00', to: '17:00' },
    { mode: 'all', from: '09:00', to: '17:00' },
    { mode: 'all', from: '09:00', to: '17:00' },
    { mode: 'off', from: '09:00', to: '17:00' },
    { mode: 'urgent', from: '09:00', to: '17:00' },
];
export function minutesToDuration(total) {
    if (total === null)
        return null;
    const days = Math.floor(total / 1440);
    const hours = Math.floor((total % 1440) / 60);
    return { days, hours, minutes: total % 60 };
}
export function durationToMinutes(d) {
    return d.days * 1440 + d.hours * 60 + d.minutes;
}
/** "2d 4h", "45m", "1d" — a target or a remaining time, compactly. */
export function formatSupportDuration(totalMinutes) {
    const d = minutesToDuration(Math.max(0, Math.round(totalMinutes)));
    const parts = [];
    if (d.days)
        parts.push(`${d.days}d`);
    if (d.hours)
        parts.push(`${d.hours}h`);
    if (d.minutes || parts.length === 0)
        parts.push(`${d.minutes}m`);
    return parts.join(' ');
}
/** Statuses a ticket is finished in. A stage counting as one is an end stage. */
export const SUPPORT_DONE_STATUSES = ['resolved', 'closed'];
export function isDoneStatus(status) {
    return SUPPORT_DONE_STATUSES.includes(status);
}
// ─── Workflow: the fields a stage needs ──────────────────────────────────────
/**
 * The ticket fields every install has. A stage can make any of them
 * mandatory (a ticket cannot move into the stage until it is filled) or
 * optional (shown, never blocking). The priority is not here: a ticket
 * always has one.
 */
export const SUPPORT_TICKET_FIELDS = [
    { key: 'subject', label: 'Subject' },
    { key: 'summary', label: 'Summary' },
    { key: 'assignee', label: 'Assignee' },
    { key: 'contract', label: 'Contract ID, or "No contract ID"' },
    { key: 'contractId', label: 'Contract ID (a linked contract)' },
    { key: 'serviceType', label: 'Service type' },
    { key: 'customerName', label: 'Customer name' },
    { key: 'customerEmail', label: 'Customer email' },
    { key: 'customerPhone', label: 'Customer phone' },
    { key: 'timeLogged', label: 'Time logged (at least one line)' },
];
/** A custom field's key in a stage's rules. */
export function customFieldKey(customFieldId) {
    return `custom:${customFieldId}`;
}
export const SUPPORT_CUSTOM_FIELD_TYPES = [
    'text',
    'long_text',
    'number',
    'date',
    'select',
    'checkbox',
    'attachment',
];
/** UST-3195 — which files an "attachment" field takes. */
export const SUPPORT_FILE_KIND_RULES = ['any', 'image', 'pdf', 'document'];
export const SUPPORT_FILE_KIND_RULE_LABELS = {
    any: 'Any file',
    image: 'Images (photos, screenshots)',
    pdf: 'PDF only',
    document: 'Documents (not images, audio or video)',
};
/** Whether a file of this kind and type counts for a field's rule. */
export function fileMatchesKindRule(rule, file) {
    switch (rule ?? 'any') {
        case 'image':
            return file.kind === 'image';
        case 'pdf':
            return file.mimeType === 'application/pdf';
        case 'document':
            return file.kind === 'file';
        default:
            return true;
    }
}
export const SUPPORT_CUSTOM_FIELD_TYPE_LABELS = {
    text: 'Text',
    long_text: 'Long text',
    number: 'Number',
    date: 'Date',
    select: 'Dropdown',
    checkbox: 'Checkbox',
    attachment: 'Files',
};
/** Whether a custom field's value counts as filled. A checkbox must be ticked. */
export function isCustomValueFilled(value) {
    if (value === null || value === undefined)
        return false;
    if (typeof value === 'string')
        return value.trim().length > 0;
    if (typeof value === 'number')
        return Number.isFinite(value);
    return value;
}
//# sourceMappingURL=support-workflow.js.map